import { debounce } from 'lodash'
import { useEffect, useState } from 'react'
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi2'
import { fetchEmployees } from 'services/fetch-employees'
import { MainTemplate } from 'templates/MainTemplate'
import { EmployeesResponse, Links } from 'types/employee'

import { ModalEmployees } from './components/modal'
import { SelectAddEmployees } from './components/SelectAddEmployees'
import { SelectStatus } from './components/SelectStatus'
import { Table } from './components/table'

export default function Employees() {
  const [employees, setEmployees] = useState<EmployeesResponse>(
    {} as EmployeesResponse,
  )
  const [links, setLinks] = useState<Links>({} as Links)
  const [currentPage, setCurrentPage] = useState(1)
  const [pages, setPages] = useState<number[]>([])
  const [clicked, setClicked] = useState(1)
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState('Status')
  const [searchEmail, setSearchEmail] = useState('')
  const [modalEmployees, setModalEmployees] = useState(false)

  const optionsStatus = [
    { value: 'Inativo', label: 'Inativo' },
    { value: 'Sem plano', label: 'Sem plano' },
    { value: 'Com plano', label: 'Com plano' },
    { value: 'Desconto em folha', label: 'Desconto em folha' },
  ]

  const optionsAddEmployee = [
    { value: 'Manual', label: 'Manual' },
    { value: 'Em lote', label: 'Em lote' },
  ]

  useEffect(() => {
    setLoading(true)

    if (selectedOption === 'Status') {
      setCurrentPage(1)
      setClicked(1)
    }

    const isSelectStatus = selectedOption !== 'Status'
    fetchEmployees({
      subscriptionStatus: isSelectStatus
        ? selectedOption.toLowerCase()
        : undefined,
      email: searchEmail || undefined,
    })
      .then((response) => {
        setEmployees(response)
        setLinks(response.links)
        setPages(
          Array.from({ length: response.links.total_pages }, (_, i) => i + 1),
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [selectedOption, searchEmail])

  const handlePagination = (link: string) => {
    return () => {
      setLoading(true)

      if (selectedOption !== 'Status') {
        setCurrentPage(1)
        setClicked(1)
      }

      switch (link) {
        case links.first:
          setCurrentPage(1)
          setClicked(1)
          break
        case links.prev:
          setCurrentPage((prev) => prev - 1)
          setClicked((prev) => prev - 1)
          break
        case links.next:
          setCurrentPage((prev) => prev + 1)
          setClicked((prev) => prev + 1)
          break
        case links.last:
          setCurrentPage(pages.length)
          setClicked(pages.length)
          break
        default:
          break
      }

      fetchEmployees({ link })
        .then((response) => {
          setEmployees(response)
          setLinks(response.links)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const handleClickPage = (page: number) => {
    return () => {
      setClicked(page)
      setCurrentPage(page)
      setLoading(true)

      fetchEmployees({
        offset: (page - 1) * 10,
        subscriptionStatus:
          selectedOption !== 'Status'
            ? selectedOption.toLowerCase()
            : undefined,
      })
        .then((response) => {
          setEmployees(response)
          setLinks(response.links)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const debouncedSetSearchEmail = debounce((value: string) => {
    if (value.length >= 5) {
      setSearchEmail(value)
    }
    if (value.length < 5 && searchEmail) {
      setCurrentPage(1)
      setSearchEmail('')
      setLinks({} as Links)
      setSelectedOption('Status')
    }
  }, 500)

  const totalPages = pages.length

  let startIndex = Math.max(currentPage - 2, 0)
  const endIndex = Math.min(startIndex + 2, totalPages - 1)

  // Se não houver páginas suficientes à direita, ajusta o início e o fim
  if (endIndex === totalPages - 1) {
    startIndex = Math.max(endIndex - 2, 0)
  }

  const visiblePages = pages.slice(startIndex, endIndex + 1)

  return (
    <MainTemplate>
      {modalEmployees && (
        <ModalEmployees setModalEmployees={setModalEmployees} />
      )}
      <section
        className="
          w-[1240px] flex justify-center items-center
          bg-neutral-100 dark:bg-neutral-800 rounded-2xl
          py-4 h-full my-10 mx-auto
        "
      >
        <div className="w-[850px] h-full flex justify-center items-center flex-col py-6 px-4">
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center justify-start w-full gap-2">
              <input
                type="email"
                placeholder="Buscar por email"
                className="px-4 py-3 border w-[18rem] hover:border-[#4F46E5] focus:border-[#4F46E5]"
                onChange={(e) => debouncedSetSearchEmail(e.target.value)}
              />
              <div className="flex items-center w-full gap-2">
                <SelectStatus
                  optionsList={optionsStatus}
                  selectedOption={selectedOption}
                  setStatedSelectedOption={setSelectedOption}
                />
                <button
                  type="button"
                  className="text-center text-sm w-1/3 text-neutral-500 underline"
                  onClick={() => {
                    setSearchEmail('')
                    setSelectedOption('Status')
                  }}
                >
                  Limpar Filtro
                </button>
                <SelectAddEmployees
                  setManualModal={setModalEmployees}
                  optionsList={optionsAddEmployee}
                />
              </div>
            </div>
          </div>
          <div
            className="
              mt-8 w-full bg-white dark:bg-neutral-900
              rounded-xl border dark:border-neutral-700
              shadow-sm overflow-hidden mb-2
            "
          >
            {loading ? (
              <div className="flex justify-center items-center h-[calc(100vh-19.8rem)]">
                <h2 className="text-2xl text-neutral-500 dark:text-neutral-400">
                  Carregando funcionários...
                </h2>
              </div>
            ) : (
              <Table data={employees?.data} />
            )}
            {employees?.data?.length === 0 && (
              <div className="flex justify-center items-center h-[calc(100vh-19.8rem)]">
                <h2 className="text-2xl text-neutral-500 dark:text-neutral-400">
                  Nenhum funcionário encontrado
                </h2>
              </div>
            )}
          </div>
          {!loading && employees.data.length >= 1 ? (
            <div
              className={`
                "flex justify-start items-start w-full text-start"
              `}
            >
              <p className="text-sm ml-2">
                Mostrando {employees?.data?.length} de{' '}
                {employees?.total_employee}
              </p>
            </div>
          ) : null}
          {pages.length > 1 && !searchEmail ? (
            <div className="flex justify-center items-center mt-2 gap-2">
              <button
                className="p-2"
                disabled={currentPage === 1}
                onClick={handlePagination(links.first)}
              >
                <HiChevronDoubleLeft
                  className={`
                    size-6
                    ${links.first === undefined ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                    ${currentPage === 1 ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                  `}
                />
              </button>
              <button
                className="p-2"
                disabled={links.prev === undefined}
                onClick={handlePagination(links.prev)}
              >
                <HiChevronLeft
                  className={`
                    size-6
                    ${
                      links.prev === undefined
                        ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed'
                        : ''
                    }
                  `}
                />
              </button>
              <ul className="flex justify-center items-center gap-4">
                {visiblePages.map((page) => (
                  <li key={`pagination-${page}`}>
                    <button
                      type="button"
                      className={`
                        w-10 h-10 flex items-center justify-center
                        border border-neutral-200 dark:border-neutral-700
                        rounded-full text-neutral-500 dark:text-neutral-300
                        ${clicked === page ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : ''}
                      `}
                      onClick={handleClickPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                className="p-2"
                disabled={
                  links.next === undefined || currentPage === pages.length
                }
                onClick={handlePagination(links.next)}
              >
                <HiChevronRight
                  className={`
                    size-6
                    ${links.next === undefined ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                    ${currentPage === pages.length ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                  `}
                />
              </button>
              <button
                className="p-2"
                disabled={
                  links.last === undefined || currentPage === pages.length
                }
                onClick={handlePagination(links.last)}
              >
                <HiChevronDoubleRight
                  className={`
                    size-6
                    ${links.last === undefined ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                    ${currentPage === pages.length ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : ''}
                  `}
                />
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </MainTemplate>
  )
}
